import { useSyncExternalStore } from 'react';

/**
 * Цветная плашка подборки или баннера. Цвет приходит из админки светлым hex; в тёмной схеме он
 * затемняется, а текст выбирается по настоящей яркости заливки: иначе светлый текст тёмной
 * схемы пропадает на пастели.
 */
export type Plate = { fill: string; onDark: boolean };

type Rgb = [number, number, number];

export function parseHex(hex: string | null | undefined): Rgb | null {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

const DARK_FACTOR = 0.32;

/** `fallback` — CSS-цвет пастели из темы, когда своего цвета у плашки нет. */
export function plateOf(hex: string | null | undefined, fallback: string, dark: boolean): Plate {
  const rgb = parseHex(hex);
  if (!rgb) return { fill: fallback, onDark: false };
  if (dark) return { fill: `rgb(${rgb.map((channel) => Math.round(channel * DARK_FACTOR)).join(' ')})`, onDark: false };
  const luminance = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  return { fill: `rgb(${rgb.join(' ')})`, onDark: luminance <= 0.5 };
}

const darkQuery = typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: dark)');

export function useDarkScheme(): boolean {
  return useSyncExternalStore(
    (listener) => {
      darkQuery?.addEventListener('change', listener);
      return () => darkQuery?.removeEventListener('change', listener);
    },
    () => darkQuery?.matches ?? false,
  );
}
